import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Calculator, Calendar as CalendarIcon, FolderArchive, Table, ScanLine, Image, Sparkles,
  Plus, Minus, X, Divide, Delete, ChevronLeft, ChevronRight, ArrowLeftRight,
  Upload, FileText, Camera, Folder, File, Download, RefreshCw, CheckCircle, Trash2,
  DollarSign, AlertCircle, Check, XCircle, Building2, Eye, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import BulkUpload from '../components/BulkUpload';
import OCRScanner from '../components/OCRScanner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ToolsPage = () => {
  const location = useLocation();
  const path = location.pathname;

  const getContent = () => {
    if (path.includes('/tools/calculator')) return <CalculatorCalendar />;
    if (path.includes('/tools/file-manager')) return <FileManager />;
    if (path.includes('/tools/spreadsheet')) return <SpreadsheetWorkspace />;
    if (path.includes('/tools/scanner')) return <ScannerTool />;
    if (path.includes('/tools/doc-to-txn')) return <DocToTransaction />;
    if (path.includes('/tools/ocr-assist')) return <OCRAssist />;
    if (path.includes('/tools/bank-reconciliation')) return <BankReconciliation />;
    return <ToolsOverview />;
  };

  return <div className="space-y-6">{getContent()}</div>;
};

// Overview
const ToolsOverview = () => {
  const navigate = useNavigate();
  const tools = [
    { icon: Calculator, label: 'Calculator / Calendar', path: '/tools/calculator', description: 'Quick calculations and date lookup' },
    { icon: FolderArchive, label: 'File Manager', path: '/tools/file-manager', description: 'Manage imports, exports, and documents' },
    { icon: Table, label: 'Spreadsheet Workspace', path: '/tools/spreadsheet', description: 'View and edit budgets, mappings' },
    { icon: ScanLine, label: 'Scanner / Camera', path: '/tools/scanner', description: 'Scan documents and photos' },
    { icon: ArrowLeftRight, label: 'Bank Reconciliation', path: '/tools/bank-reconciliation', description: 'Reconcile bank statements with books' },
    { icon: Sparkles, label: 'AI Document-to-Transaction', path: '/tools/doc-to-txn', description: 'Upload bill → AI drafts AP entry', isAI: true },
    { icon: Image, label: 'AI OCR Assist', path: '/tools/ocr-assist', description: 'Extract data from images', isAI: true },
  ];

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tools</h1>
        <p className="text-gray-500 mt-1">Utilities and AI-powered assistants</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {tools.map((tool, idx) => {
          const Icon = tool.icon;
          return (
            <Card 
              key={idx} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${tool.isAI ? 'bg-purple-50/50 border-purple-200' : ''}`}
              onClick={() => navigate(tool.path)}
              data-testid={`tool-${tool.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className={`p-3 rounded-lg inline-block mb-3 ${tool.isAI ? 'bg-purple-100' : 'bg-slate-100'}`}>
                  <Icon className={`w-6 h-6 ${tool.isAI ? 'text-purple-600' : 'text-slate-600'}`} />
                </div>
                <h3 className="font-semibold text-gray-900">{tool.label}</h3>
                <p className="text-sm text-gray-500 mt-1">{tool.description}</p>
                {tool.isAI && (
                  <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded mt-2 inline-block">AI Powered</span>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};

// ==================== CALCULATOR & CALENDAR ====================
const CalculatorCalendar = () => {
  const [display, setDisplay] = useState('0');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [history, setHistory] = useState([]);

  const handleCalcClick = (value) => {
    if (value === 'C') {
      setDisplay('0');
    } else if (value === 'CE') {
      setHistory([]);
      setDisplay('0');
    } else if (value === '=') {
      try {
        const result = eval(display.replace(/×/g, '*').replace(/÷/g, '/'));
        setHistory([...history, `${display} = ${result}`]);
        setDisplay(String(result));
      } catch {
        setDisplay('Error');
      }
    } else if (value === '←') {
      setDisplay(display.length > 1 ? display.slice(0, -1) : '0');
    } else if (value === '±') {
      setDisplay(display.startsWith('-') ? display.slice(1) : '-' + display);
    } else if (value === '%') {
      setDisplay(String(parseFloat(display) / 100));
    } else {
      setDisplay(display === '0' || display === 'Error' ? value : display + value);
    }
  };

  const calcButtons = [
    { label: 'CE', className: 'bg-red-100 text-red-700' },
    { label: 'C', className: 'bg-amber-100 text-amber-700' },
    { label: '←', className: 'bg-slate-200' },
    { label: '÷', className: 'bg-sky-100 text-sky-700' },
    { label: '7' }, { label: '8' }, { label: '9' },
    { label: '×', className: 'bg-sky-100 text-sky-700' },
    { label: '4' }, { label: '5' }, { label: '6' },
    { label: '-', className: 'bg-sky-100 text-sky-700' },
    { label: '1' }, { label: '2' }, { label: '3' },
    { label: '+', className: 'bg-sky-100 text-sky-700' },
    { label: '±' }, { label: '0' }, { label: '.' },
    { label: '=', className: 'bg-emerald-500 text-white' },
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const today = () => setCurrentDate(new Date());

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Calculator / Calendar</h1>
        <p className="text-gray-500 mt-1">Quick calculations and date reference</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Calculator */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Calculator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-900 rounded-lg p-4 mb-4">
              <div className="text-right text-slate-400 text-sm h-6 overflow-hidden">
                {history.length > 0 && history[history.length - 1]}
              </div>
              <div className="text-right text-white text-3xl font-mono overflow-hidden">
                {display}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {calcButtons.map((btn, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  className={`h-12 text-lg font-medium ${btn.className || ''}`}
                  onClick={() => handleCalcClick(btn.label === '×' ? '*' : btn.label === '÷' ? '/' : btn.label)}
                  data-testid={`calc-${btn.label}`}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
            {history.length > 0 && (
              <div className="mt-4 p-2 bg-slate-50 rounded text-sm text-gray-600 max-h-20 overflow-y-auto">
                {history.slice(-3).map((h, i) => <div key={i}>{h}</div>)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Calendar
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={today}>Today</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="sm" onClick={prevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <Button variant="ghost" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-xs font-medium text-gray-500 py-2">{day}</div>
              ))}
              {days.map((day, idx) => {
                const isToday = day === new Date().getDate() && 
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();
                return (
                  <div 
                    key={idx} 
                    className={`py-2 text-sm rounded cursor-pointer transition-colors ${
                      isToday
                        ? 'bg-sky-500 text-white font-bold' 
                        : day ? 'hover:bg-gray-100' : ''
                    }`}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Today: <span className="font-semibold">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// ==================== FILE MANAGER ====================
const FileManager = () => {
  const [files, setFiles] = useState([
    { id: 1, name: 'Q4_Expenses.xlsx', type: 'import', date: '2026-01-20', size: '245 KB' },
    { id: 2, name: 'Payroll_Jan2026.csv', type: 'import', date: '2026-01-15', size: '128 KB' },
    { id: 3, name: 'Balance_Sheet_2025.pdf', type: 'export', date: '2026-01-10', size: '1.2 MB' },
    { id: 4, name: 'Vendor_W9_ABC.pdf', type: 'document', date: '2026-01-05', size: '89 KB' },
  ]);
  const [activeFolder, setActiveFolder] = useState('all');
  const [uploading, setUploading] = useState(false);

  const handleUpload = () => {
    setUploading(true);
    setTimeout(() => {
      const newFile = {
        id: files.length + 1,
        name: `Upload_${Date.now()}.csv`,
        type: 'import',
        date: new Date().toISOString().split('T')[0],
        size: `${Math.floor(Math.random() * 500 + 50)} KB`
      };
      setFiles([newFile, ...files]);
      setUploading(false);
    }, 1000);
  };

  const handleDelete = (id) => {
    setFiles(files.filter(f => f.id !== id));
  };

  const filteredFiles = activeFolder === 'all' ? files : files.filter(f => f.type === activeFolder);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File Manager</h1>
          <p className="text-gray-500 mt-1">Manage your imports, exports, and documents</p>
        </div>
        <Button onClick={handleUpload} disabled={uploading} data-testid="upload-file-btn">
          {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          Upload File
        </Button>
      </div>

      <div className="flex gap-4">
        <Card className="w-48">
          <CardContent className="p-4 space-y-1">
            {[
              { id: 'all', label: 'All Files', icon: Folder },
              { id: 'import', label: 'Imports', icon: Download },
              { id: 'export', label: 'Exports', icon: Upload },
              { id: 'document', label: 'Documents', icon: FileText },
            ].map((folder) => {
              const Icon = folder.icon;
              return (
                <button
                  key={folder.id}
                  onClick={() => setActiveFolder(folder.id)}
                  className={`w-full flex items-center gap-2 p-2 text-left rounded-md text-sm transition-colors ${
                    activeFolder === folder.id ? 'bg-sky-50 text-sky-600 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />{folder.label}
                </button>
              );
            })}
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Name</th>
                  <th className="text-left p-3 font-medium text-gray-600">Type</th>
                  <th className="text-left p-3 font-medium text-gray-600">Date</th>
                  <th className="text-left p-3 font-medium text-gray-600">Size</th>
                  <th className="text-right p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredFiles.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-gray-500">No files in this folder</td></tr>
                ) : filteredFiles.map((file) => (
                  <tr key={file.id} className="hover:bg-slate-50">
                    <td className="p-3 flex items-center gap-2">
                      <File className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{file.name}</span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${file.type === 'import' ? 'bg-sky-100 text-sky-700' :
                          file.type === 'export' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                        {file.type}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500">{file.date}</td>
                    <td className="p-3 text-gray-500">{file.size}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm"><Download className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(file.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// ==================== SPREADSHEET WORKSPACE ====================
const SpreadsheetWorkspace = () => {
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [data, setData] = useState([
    ['Account', 'Budget', 'Actual', 'Variance'],
    ['6000 - Salaries', '$50,000', '$48,500', '$1,500'],
    ['6100 - Rent', '$12,000', '$12,000', '$0'],
    ['6200 - Utilities', '$3,000', '$2,800', '$200'],
    ['6300 - Supplies', '$5,000', '$5,200', '-$200'],
  ]);

  const handleCellChange = (rowIdx, colIdx, value) => {
    const newData = [...data];
    newData[rowIdx][colIdx] = value;
    setData(newData);
  };

  const addRow = () => {
    setData([...data, ['', '', '', '']]);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Spreadsheet Workspace</h1>
          <p className="text-gray-500 mt-1">View and edit budgets, mappings, and data</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addRow}><Plus className="w-4 h-4 mr-2" />Add Row</Button>
          <Button onClick={() => setShowBulkUpload(true)}>
            <Upload className="w-4 h-4 mr-2" />Import
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full border-collapse">
            <tbody>
              {data.map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx === 0 ? 'bg-slate-100' : 'hover:bg-slate-50'}>
                  {row.map((cell, colIdx) => (
                    <td key={colIdx} className="border border-slate-200 p-0">
                      <input
                        type="text"
                        value={cell}
                        onChange={(e) => handleCellChange(rowIdx, colIdx, e.target.value)}
                        className={`w-full p-2 outline-none focus:bg-sky-50 ${
                          rowIdx === 0 ? 'font-semibold bg-slate-100' : ''
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="text-sm text-gray-500">
        Click on any cell to edit. Changes are saved automatically.
      </div>

      <BulkUpload isOpen={showBulkUpload} onClose={() => setShowBulkUpload(false)} />
    </>
  );
};

// ==================== SCANNER TOOL ====================
const ScannerTool = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const res = await fetch(`${API_URL}/api/scanner/analyze`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        toast.success(`Document classified as "${data.document_type}" and saved to ${data.saved_to}`);
        fetchHistory();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Analysis failed');
      }
    } catch (err) {
      toast.error('Failed to analyze document');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scanner/history?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.scans || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  React.useEffect(() => { fetchHistory(); }, []);

  const getTypeIcon = (type) => {
    const icons = {
      receipt: '🧾', invoice: '📄', equipment: '🚜', id_card: '🪪',
      timesheet: '⏱️', check: '📝', contract: '📑', w2_tax: '📋', other: '📎'
    };
    return icons[type] || '📎';
  };

  const getTypeColor = (type) => {
    const colors = {
      receipt: 'bg-amber-100 text-amber-700', invoice: 'bg-sky-100 text-sky-700',
      equipment: 'bg-emerald-100 text-emerald-700', id_card: 'bg-purple-100 text-purple-700',
      timesheet: 'bg-pink-100 text-pink-700', check: 'bg-indigo-100 text-indigo-700',
      contract: 'bg-slate-100 text-slate-700', w2_tax: 'bg-red-100 text-red-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Document Scanner</h1>
          <p className="text-gray-500 mt-1">Scan, OCR, classify, and route documents automatically</p>
        </div>
      </div>

      {/* AI Info Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-sky-50 border-purple-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Sparkles className="w-10 h-10 text-purple-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-purple-900">AI-Powered OCR & Classification</h3>
            <p className="text-sm text-purple-700">Upload any document. AI will extract text, classify it, and save to the correct location (receipts, equipment, timesheets, etc.)</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Upload Document</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf"
              className="hidden"
            />
            
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50 transition-colors"
            >
              {preview ? (
                <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
              ) : (
                <>
                  <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Click or drag to upload</p>
                  <p className="text-xs text-gray-400 mt-1">JPEG, PNG, PDF supported</p>
                </>
              )}
            </div>

            {selectedFile && (
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm truncate">{selectedFile.name}</span>
                <Button onClick={handleAnalyze} disabled={loading}>
                  {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analyzing...</> : <><Sparkles className="w-4 h-4 mr-1" /> Analyze with AI</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Analysis Result</CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-center py-8 text-gray-500">
                <Eye className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>Upload and analyze a document to see results</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getTypeIcon(result.document_type)}</span>
                  <div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(result.document_type)}`}>
                      {result.document_type?.replace('_', ' ').toUpperCase()}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Confidence: {(result.confidence * 100).toFixed(0)}%</p>
                  </div>
                </div>

                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700"><strong>Saved to:</strong> {result.saved_to}</p>
                  <p className="text-sm text-emerald-700"><strong>Document ID:</strong> {result.document_id}</p>
                </div>

                {result.extracted_data && Object.keys(result.extracted_data).length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">Extracted Data:</h4>
                    <div className="space-y-1 text-sm">
                      {Object.entries(result.extracted_data).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span>
                          <span className="font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.raw_text && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">OCR Text:</h4>
                    <p className="text-xs text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">{result.raw_text}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Document Types Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Supported Document Types & Destinations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { type: 'receipt', dest: 'Receipts', icon: '🧾' },
              { type: 'invoice', dest: 'Invoices', icon: '📄' },
              { type: 'equipment', dest: 'Equipment', icon: '🚜' },
              { type: 'id_card', dest: 'Employee Docs', icon: '🪪' },
              { type: 'timesheet', dest: 'Time Entries', icon: '⏱️' },
              { type: 'check', dest: 'Checks', icon: '📝' },
              { type: 'contract', dest: 'Contracts', icon: '📑' },
              { type: 'w2_tax', dest: 'Tax Documents', icon: '📋' },
              { type: 'other', dest: 'Scanned Docs', icon: '📎' },
            ].map(d => (
              <div key={d.type} className="p-3 bg-slate-50 rounded-lg text-center">
                <span className="text-2xl">{d.icon}</span>
                <p className="text-xs font-medium mt-1">{d.type.replace('_', ' ')}</p>
                <p className="text-xs text-gray-500">→ {d.dest}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scan History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Scans</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((scan, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex items-center gap-3">
                    <span>{getTypeIcon(scan.document_type)}</span>
                    <div>
                      <p className="font-medium text-sm">{scan.scan_id}</p>
                      <p className="text-xs text-gray-500">{scan.original_filename}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(scan.document_type)}`}>
                    {scan.document_type}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// ==================== AI DOCUMENT TO TRANSACTION ====================
const DocToTransaction = () => {
  const [showOCRScanner, setShowOCRScanner] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = () => {
    setProcessing(true);
    setTimeout(() => {
      setResult({
        vendor: 'ABC Supplies Co',
        amount: 1250.00,
        date: '2026-01-20',
        invoiceNumber: 'INV-2026-0123',
        suggestedAccount: '6200 - Office Supplies',
        confidence: 95
      });
      setProcessing(false);
    }, 2000);
  };

  const handleCreateTransaction = () => {
    alert(`Creating AP entry for ${result.vendor}: $${result.amount}`);
    setResult(null);
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-500" />
          AI Document-to-Transaction
        </h1>
        <p className="text-gray-500 mt-1">Upload a document and AI will create the transaction</p>
      </div>

      {!result ? (
        <Card className="bg-gradient-to-br from-purple-50 to-sky-50 border-purple-200">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-10 h-10 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Upload a Bill or Invoice</h2>
            <p className="text-gray-600 mt-2 max-w-md mx-auto">
              AI will extract vendor, amount, date, and GL coding to create an AP entry automatically
            </p>
            <Button 
              className="mt-6 bg-purple-600 hover:bg-purple-700"
              onClick={handleUpload}
              disabled={processing}
              data-testid="upload-doc-btn"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Upload Document</>
              )}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              AI Extracted Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-500">Vendor</p>
                <p className="font-semibold">{result.vendor}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-500">Amount</p>
                <p className="font-semibold text-lg">${result.amount.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-500">Invoice #</p>
                <p className="font-semibold">{result.invoiceNumber}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-semibold">{result.date}</p>
              </div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-500">Suggested GL Account</p>
              <p className="font-semibold">{result.suggestedAccount}</p>
              <p className="text-xs text-purple-600 mt-1">{result.confidence}% confidence</p>
            </div>
            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setResult(null)}>Cancel</Button>
              <Button onClick={handleCreateTransaction} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Create AP Entry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">95%</p>
            <p className="text-sm text-gray-500">Accuracy Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">2s</p>
            <p className="text-sm text-gray-500">Avg. Processing</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">24</p>
            <p className="text-sm text-gray-500">Docs This Month</p>
          </CardContent>
        </Card>
      </div>

      <OCRScanner isOpen={showOCRScanner} onClose={() => setShowOCRScanner(false)} />
    </>
  );
};

// ==================== OCR ASSIST ====================
const OCRAssist = () => {
  const [showOCRScanner, setShowOCRScanner] = useState(false);
  const [extractedText, setExtractedText] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleExtract = () => {
    setProcessing(true);
    setTimeout(() => {
      setExtractedText({
        raw: 'ABC SUPPLIES CO\n123 Main Street\nFarmville, CA 12345\n\nInvoice #: INV-2026-0123\nDate: January 20, 2026\n\nOffice Supplies........$450.00\nPrinter Paper...........$125.00\nInk Cartridges..........$175.00\nMisc Items..............$500.00\n\nSubtotal: $1,250.00\nTax: $0.00\nTotal Due: $1,250.00',
        structured: {
          company: 'ABC Supplies Co',
          address: '123 Main Street, Farmville, CA 12345',
          invoiceNumber: 'INV-2026-0123',
          date: 'January 20, 2026',
          total: '$1,250.00'
        }
      });
      setProcessing(false);
    }, 1500);
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Image className="w-6 h-6 text-purple-500" />
          AI OCR Assist
        </h1>
        <p className="text-gray-500 mt-1">Extract text and data from images</p>
      </div>

      <Card className="bg-gradient-to-br from-purple-50 to-sky-50 border-purple-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="p-4 bg-purple-100 rounded-xl">
              <Image className="w-12 h-12 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-lg">Intelligent OCR</h3>
              <p className="text-gray-600 mt-1">
                Extract data from business cards, IDs, vendor cards, equipment photos, and more.
              </p>
            </div>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleExtract}
              disabled={processing}
              data-testid="start-ocr-btn"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                <><ScanLine className="w-4 h-4 mr-2" />Start OCR</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {extractedText && (
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Raw Text</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-50 p-4 rounded-lg text-sm whitespace-pre-wrap font-mono">
                {extractedText.raw}
              </pre>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Structured Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(extractedText.structured).map(([key, value]) => (
                <div key={key} className="flex justify-between p-2 bg-slate-50 rounded">
                  <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
              <Button className="w-full mt-4">Use This Data</Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Supported Document Types</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" />Invoices & Bills</li>
              <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" />Receipts</li>
              <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" />Timecards</li>
              <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" />Business Cards</li>
              <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" />ID Documents</li>
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">AI Capabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Auto-detect document type</li>
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Extract structured data</li>
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Suggest GL account coding</li>
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Create records automatically</li>
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Confidence scoring</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <OCRScanner isOpen={showOCRScanner} onClose={() => setShowOCRScanner(false)} />
    </>
  );
};

// ==================== BANK RECONCILIATION ====================
const BankReconciliation = () => {
  const navigate = useNavigate();
  const [bankBalance, setBankBalance] = useState('');
  const [statementDate, setStatementDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookBalance, setBookBalance] = useState(125000);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [reconciled, setReconciled] = useState(false);

  useEffect(() => {
    fetchTransactions();
    fetchBookBalance();
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/banking/transactions?status=unreconciled`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTransactions(data);
      } else {
        // Use sample data if no banking data
        setTransactions([
          { id: 'TXN001', date: '2026-01-15', description: 'Check #1042 - ABC Supply', amount: -2500, type: 'check', status: 'unreconciled' },
          { id: 'TXN002', date: '2026-01-16', description: 'Deposit - Customer Payment', amount: 8500, type: 'deposit', status: 'unreconciled' },
          { id: 'TXN003', date: '2026-01-18', description: 'Check #1043 - Utilities', amount: -450, type: 'check', status: 'unreconciled' },
          { id: 'TXN004', date: '2026-01-20', description: 'ACH - Payroll', amount: -12500, type: 'ach', status: 'unreconciled' },
          { id: 'TXN005', date: '2026-01-22', description: 'Wire - Customer XYZ', amount: 15000, type: 'wire', status: 'unreconciled' },
          { id: 'TXN006', date: '2026-01-23', description: 'Check #1044 - Insurance', amount: -1200, type: 'check', status: 'unreconciled' },
          { id: 'TXN007', date: '2026-01-24', description: 'Bank Fee', amount: -35, type: 'fee', status: 'unreconciled' },
        ]);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookBalance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBookBalance(data.cash_balance || 125000);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const toggleTransaction = (id) => {
    const newSelected = new Set(selectedTxns);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTxns(newSelected);
  };

  const selectAll = () => {
    if (selectedTxns.size === transactions.length) {
      setSelectedTxns(new Set());
    } else {
      setSelectedTxns(new Set(transactions.map(t => t.id)));
    }
  };

  const calculateDifference = () => {
    const statementBal = parseFloat(bankBalance) || 0;
    const clearedTotal = transactions
      .filter(t => selectedTxns.has(t.id))
      .reduce((sum, t) => sum + t.amount, 0);
    const adjustedBook = bookBalance + clearedTotal;
    return statementBal - adjustedBook;
  };

  const handleReconcile = async () => {
    const diff = calculateDifference();
    if (Math.abs(diff) > 0.01) {
      toast.error(`Cannot reconcile. Difference of $${Math.abs(diff).toFixed(2)} exists.`);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/banking/reconciliation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: 'checking-main',
          statement_date: statementDate,
          statement_balance: parseFloat(bankBalance),
          transaction_ids: Array.from(selectedTxns)
        })
      });

      if (res.ok) {
        toast.success('Bank reconciliation completed!');
        setReconciled(true);
      } else {
        toast.error('Failed to complete reconciliation');
      }
    } catch (err) {
      toast.error('Failed to complete reconciliation');
    }
  };

  const difference = calculateDifference();
  const clearedBalance = transactions
    .filter(t => selectedTxns.has(t.id))
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bank Reconciliation</h1>
          <p className="text-gray-500 mt-1">Match your bank statement with book records</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/tools')}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Tools
        </Button>
      </div>

      {reconciled && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">Reconciliation Complete!</p>
              <p className="text-sm text-emerald-600">Bank statement for {statementDate} has been reconciled.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <Building2 className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Statement Balance</p>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">$</span>
                  <Input 
                    type="number"
                    step="0.01"
                    placeholder="Enter balance"
                    value={bankBalance}
                    onChange={(e) => setBankBalance(e.target.value)}
                    className="w-32 h-8 text-lg font-bold"
                    data-testid="statement-balance"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Book Balance</p>
            <p className="text-2xl font-bold text-gray-900">${bookBalance.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Cleared Items</p>
            <p className="text-2xl font-bold text-emerald-600">
              {selectedTxns.size} / {transactions.length}
            </p>
            <p className="text-xs text-gray-500">${clearedBalance.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className={Math.abs(difference) < 0.01 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Difference</p>
            <p className={`text-2xl font-bold ${Math.abs(difference) < 0.01 ? 'text-emerald-600' : 'text-red-600'}`}>
              ${Math.abs(difference).toFixed(2)}
            </p>
            {Math.abs(difference) < 0.01 && <p className="text-xs text-emerald-600">Balanced!</p>}
          </CardContent>
        </Card>
      </div>

      {/* Statement Date */}
      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="flex-1 flex items-center gap-4">
            <Label>Statement Date:</Label>
            <Input 
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
              className="w-40"
            />
          </div>
          <Button 
            onClick={handleReconcile}
            disabled={Math.abs(difference) > 0.01 || !bankBalance}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="complete-reconciliation"
          >
            <Check className="w-4 h-4 mr-2" /> Complete Reconciliation
          </Button>
        </CardContent>
      </Card>

      {/* Transaction List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>Unreconciled Transactions</CardTitle>
            <CardDescription>Select transactions that appear on your bank statement</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={selectAll}>
            {selectedTxns.size === transactions.length ? 'Deselect All' : 'Select All'}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading transactions...</div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No unreconciled transactions</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-y">
                <tr>
                  <th className="p-3 text-left w-12">
                    <input 
                      type="checkbox" 
                      checked={selectedTxns.size === transactions.length}
                      onChange={selectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="p-3 text-left font-medium text-gray-600">Date</th>
                  <th className="p-3 text-left font-medium text-gray-600">Description</th>
                  <th className="p-3 text-left font-medium text-gray-600">Type</th>
                  <th className="p-3 text-right font-medium text-gray-600">Amount</th>
                  <th className="p-3 text-center font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {transactions.map(txn => (
                  <tr 
                    key={txn.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${selectedTxns.has(txn.id) ? 'bg-emerald-50' : ''}`}
                    onClick={() => toggleTransaction(txn.id)}
                    data-testid={`txn-row-${txn.id}`}
                  >
                    <td className="p-3">
                      <input 
                        type="checkbox"
                        checked={selectedTxns.has(txn.id)}
                        onChange={() => toggleTransaction(txn.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3 text-gray-600">{txn.date}</td>
                    <td className="p-3 font-medium">{txn.description}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-xs rounded capitalize ${
                        txn.type === 'check' ? 'bg-blue-100 text-blue-700' :
                        txn.type === 'deposit' ? 'bg-emerald-100 text-emerald-700' :
                        txn.type === 'ach' ? 'bg-purple-100 text-purple-700' :
                        txn.type === 'wire' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {txn.type}
                      </span>
                    </td>
                    <td className={`p-3 text-right font-mono ${txn.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {txn.amount >= 0 ? '+' : ''}${Math.abs(txn.amount).toLocaleString()}
                    </td>
                    <td className="p-3 text-center">
                      {selectedTxns.has(txn.id) ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ToolsPage;
